import { useMutation } from '@tanstack/react-query';

import { logSentryError, logSentryEvent } from '@libs/common-next';

import { SentryErrors, SentryEvents } from '@/shared/sentry';

import { type CalculateFrequencyParams, type CalculateFrequencyResponse, fetchCalculateFrequency } from '../api';

type UseCalculateFrequencyParams = {
    onError?: (error: Error) => void;
    onSuccess?: (response: CalculateFrequencyResponse, variables: CalculateFrequencyParams) => void;
};

export function useCalculateFrequency({ onError, onSuccess }: UseCalculateFrequencyParams = {}) {
    return useMutation({
        mutationFn: fetchCalculateFrequency,
        onSuccess: (response, variables) => {
            logSentryEvent('Calculated autoship frequency', {
                sentryContext: SentryEvents.CALCULATE_AUTOSHIP_FREQUENCY,
                tags: {
                    component: 'FrequencyCalculator',
                    severity: 'INFO',
                },
                extra: {
                    locale: variables.locale,
                    sku: variables.id,
                    numberOfPets: String(variables.numberOfPets),
                    totalCupsPerDay: String(variables.totalCupsPerDay),
                    recommendedAutoshipFrequency: String(response.recommendedAutoshipFrequency),
                    frequencyUnit: response.frequencyUnit,
                },
            });
            onSuccess?.(response, variables);
        },
        onError: (error, variables) => {
            logSentryError(error, {
                ...SentryErrors.FrequencyCalculator.CalculateFrequencyFailed,
                extra: {
                    ...SentryErrors.FrequencyCalculator.CalculateFrequencyFailed.extra,
                    locale: variables.locale,
                    sku: variables.id,
                    numberOfPets: String(variables.numberOfPets),
                    totalCupsPerDay: String(variables.totalCupsPerDay),
                },
            });
            onError?.(error);
        },
    });
}

export type { CalculateFrequencyParams, CalculateFrequencyResponse };
